/**
 * Shop The Look Hotspots custom element
 * @example
 *  <stl-hotspots>
 *    <div data-stl-hotspots-item>
 *      <button type="button" data-stl-hotspots-target {{ block.shopify_attributes }}></button>
 *      <div data-stl-hotspots-trigger></div>
 *    </div>
 *  </stl-hotspots>
 */

if (!customElements.get('stl-hotspots')) {
  customElements.define(
    'stl-hotspots',
    class STLHotspots extends HTMLElement {
      static observedAttributes = ['class'];

      constructor() {
        super();
        this.initialOpen = this.hasAttribute('data-stl-initial-open');
      }

      get items() {
        return [...this.querySelectorAll('[data-stl-hotspots-item]')];
      }

      get targets() {
        return [...this.querySelectorAll('[data-stl-hotspots-target]')];
      }

      get triggers() {
        return [...this.querySelectorAll('[data-stl-hotspots-trigger]')];
      }

      attributeChangedCallback(name, oldValue, newValue) {
        const isClassChange = name === 'class';
        const inView = newValue.includes('aos-animate');
        let justIn = true;
        if (oldValue) {
          if (oldValue.includes('aos-animate')) justIn = false;
        }

        if (isClassChange && inView && justIn) {
          this.animate();
        }
      }

      connectedCallback() {
        this.animationsTimeout = null;
        this.resetAnimations = false;
        this.init();
        this.listen();
      }

      init() {
        if (!theme.settings.animationsEnabled) {
          this.animate();
        }

        if (!this.initialOpen) {
          this.focusables();
        }
      }

      listen() {
        this.targets.forEach((target) => {
          target.addEventListener('click', (event) => {
            const selectedIndex = this.targets.indexOf(event.target);
            this.select(selectedIndex);
            this.restoreAnimations();
          });
        });

        this.triggers.forEach((target) => {
          target.addEventListener('click', (event) => {
            const selectedIndex = this.triggers.indexOf(event.target);
            this.targets[selectedIndex].dispatchEvent(new Event('click'));
          });
        });

        this.addEventListener('keyup', (event) => this.onKeyup(event));
      }

      animate() {
        if (!this.initialOpen) return;
        let delay = 600;
        if (Shopify.designMode) delay = 300;
        if (this.resetAnimations) return;
        setTimeout(() => this.select(0), delay);
      }

      removeAnimations() {
        this.items.forEach((item) => {
          item.style.setProperty('--duration', '0s');
          item.classList.add('block-selected');
        });
        this.resetAnimations = true;
      }

      restoreAnimations() {
        if (!Shopify.designMode) return;

        this.items.forEach((item) => item.classList.remove('block-selected'));

        this.animationsTimeout = setTimeout(() => {
          this.items.forEach((item) => {
            item.style.removeProperty('--duration');
          });
          this.resetAnimations = false;
        }, 1e3);
      }

      deselectAll(event = false) {
        this.items.forEach((item, index) => {
          item.classList.remove('is-active');

          if (index === 0 && this.initialOpen && event && !this.resetAnimations) {
            item.classList.add('is-active');
          }
        });

        setTimeout(() => this.focusables());
      }

      select(selectedIndex) {
        this.items.forEach((item, index) => {
          item.setAttribute('aria-current', selectedIndex === index ? 'true' : 'false');
          item.classList.toggle('is-active', selectedIndex === index);
        });

        this.focusables();
      }

      focusables() {
        this.items.forEach((element, index) => {
          const isActive = element.classList.contains('is-active');
          const tabIndex = isActive ? '0' : '-1';
          const expanded = isActive ? true : false;
          element.querySelectorAll('[data-product-block] a, [data-product-block] button').forEach((link) => link.setAttribute('tabindex', tabIndex));
          this.targets[index].setAttribute('aria-expanded', expanded);
        });
      }

      onKeyup(event) {
        if (event.code === theme.keyboardKeys.ESCAPE) {
          this.deselectAll();
          event.target.closest('[data-stl-hotspots-item]').querySelector('[data-stl-hotspots-target]').focus();
        }
      }
    }
  );
}

/**
 * Shop The Look custom element
 * @example
 *  <shop-the-look>
 *    <stl-hotspots></stl-hotspots>
 *  </shop-the-look>
 */

if (!customElements.get('shop-the-look')) {
  customElements.define(
    'shop-the-look',
    class ShopTheLook extends HTMLElement {
      constructor() {
        super();
      }

      get hotspots() {
        return this.querySelector('stl-hotspots');
      }

      connectedCallback() {
        if (this.hotspots) {
          // Close all hotspot items when clicking outside them
          this.addEventListener('click', (event) => {
            const isHotspotsItem = Boolean(event.target.closest('[data-stl-hotspots-item]') !== null);
            if (!isHotspotsItem) {
              this.hotspots.deselectAll();
              this.hotspots.restoreAnimations();
            }
          });
        }
      }

      disconnectedCallback() {}
    }
  );
}
